<template>
  <div>
    <div class="ch-head">
      <div>
        <h1>培训管理</h1>
        <p>课程库、学习进度、考核统计、补考与证书。题库只由总部维护，不及格只催办，不冻结账号。</p>
      </div>
      <div class="ch-actions">
        <button class="ch-btn" type="button" @click="exportCourses">导出课程</button>
        <button class="ch-btn gold" type="button" @click="openCourse()">新增课程</button>
      </div>
    </div>
    <div class="ch-metrics">
      <div class="ch-metric"><div class="k">课程总数</div><div class="v">{{ courses.length }} <small>门</small></div><div class="s">{{ stageSummary }}</div></div>
      <div class="ch-metric"><div class="k">学习记录</div><div class="v">{{ progress.length }}</div><div class="s">看完课件才记入</div></div>
      <div class="ch-metric"><div class="k">考核通过率</div><div class="v">{{ passRate }}</div><div class="s">未通过 {{ failedAttempts }} 次可补考</div></div>
      <div class="ch-metric"><div class="k">已发证书</div><div class="v">{{ certified }}</div><div class="s">由总部上传，不自动生成</div></div>
    </div>
    <div class="ch-tabs">
      <button v-for="item in tabs" :key="item.id" type="button" :class="{ on: tab === item.id }" @click="tab = item.id">{{ item.label }}</button>
    </div>

    <div v-if="tab === 'courses'" class="ch-panel">
      <table class="ch-table">
        <thead><tr><th>课程</th><th>阶段</th><th>课时</th><th>课件</th><th>状态</th><th>操作</th></tr></thead>
        <tbody>
          <tr v-for="row in courses" :key="row.id">
            <td>{{ row.title }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td>{{ row.lessons }}</td>
            <td><button v-if="row.fileUrl" class="ch-link" type="button" @click="openFile(row.fileUrl)">查看</button><span v-else>—</span></td>
            <td><span class="ch-tag" :class="Number(row.status) === 0 ? 'muted' : 'ok'">{{ Number(row.status) === 0 ? '停用' : '启用' }}</span></td>
            <td><button class="ch-link" type="button" @click="openCourse(row)">编辑</button></td>
          </tr>
          <tr v-if="!courses.length"><td colspan="6" class="ch-empty">还没有课程</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'progress'" class="ch-panel">
      <table class="ch-table">
        <thead><tr><th>渠道</th><th>课程</th><th>阶段</th><th>进度</th></tr></thead>
        <tbody>
          <tr v-for="(row, index) in progress" :key="index">
            <td>{{ row.partnerName }}</td>
            <td>{{ row.title }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td>{{ Number(row.finishedLessons) >= Number(row.lessons) && Number(row.lessons) > 0 ? '已看完' : '未看完' }}</td>
          </tr>
          <tr v-if="!progress.length"><td colspan="4" class="ch-empty">还没有学习记录</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'bank'" class="ch-panel">
      <h2>考试配置</h2>
      <div class="configs">
        <div v-for="item in configForm" :key="item.stage" class="config">
          <b>{{ stageName(item.stage) }}</b>
          <label>抽取题目数量 <input v-model.number="item.drawCount" type="number" min="1" /></label>
          <label>答对达标数 <input v-model.number="item.passCount" type="number" min="1" /></label>
        </div>
      </div>
      <button class="ch-btn" type="button" @click="saveConfig">保存配置</button>
      <div class="bank-tools">
        <button class="ch-btn" type="button" @click="downloadTemplate">Excel 模板下载</button>
        <label class="ch-btn">批量导入<input class="file-proxy" type="file" accept=".csv,text/csv" @change="importCsv" /></label>
        <button class="ch-btn gold" type="button" @click="openQuestion()">新增题目</button>
      </div>
      <table class="ch-table">
        <thead><tr><th>题干</th><th>题型</th><th>选项</th><th>正确答案</th><th>解析</th><th>阶段</th><th>状态</th><th>操作</th></tr></thead>
        <tbody>
          <tr v-for="row in questions" :key="row.id">
            <td>{{ row.stem }}</td>
            <td>{{ typeName(row.questionType) }}</td>
            <td>{{ optionText(row) }}</td>
            <td>{{ row.answer }}</td>
            <td>{{ row.analysis || '—' }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td><span class="ch-tag" :class="Number(row.status) === 0 ? 'muted' : 'ok'">{{ Number(row.status) === 0 ? '停用' : '启用' }}</span></td>
            <td><button class="ch-link" type="button" @click="openQuestion(row)">编辑</button></td>
          </tr>
          <tr v-if="!questions.length"><td colspan="8" class="ch-empty">还没有题目</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'stats'" class="ch-panel">
      <table class="ch-table">
        <thead><tr><th>时间</th><th>渠道</th><th>阶段</th><th>得分</th><th>结果</th></tr></thead>
        <tbody>
          <tr v-for="row in attempts" :key="row.id">
            <td>{{ row.createdAt }}</td>
            <td>{{ row.partnerName || row.partnerId }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td>{{ row.score }}</td>
            <td><span class="ch-tag" :class="Number(row.passed) === 1 ? 'ok' : 'warn'">{{ Number(row.passed) === 1 ? '通过' : '未通过，可补考' }}</span></td>
          </tr>
          <tr v-if="!attempts.length"><td colspan="5" class="ch-empty">还没有考试记录</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else class="ch-panel">
      <table class="ch-table">
        <thead><tr><th>渠道</th><th>级别</th><th>证书</th><th>操作</th></tr></thead>
        <tbody>
          <tr v-for="row in partners" :key="row.id">
            <td>{{ row.name }}</td>
            <td>{{ row.levelCode }}</td>
            <td><a v-if="row.certificateUrl" :href="row.certificateUrl" target="_blank" rel="noopener">查看</a><span v-else>未上传</span></td>
            <td><button class="ch-link" type="button" @click="openCertificate(row)">上传</button></td>
          </tr>
          <tr v-if="!partners.length"><td colspan="4" class="ch-empty">还没有已通过的渠道</td></tr>
        </tbody>
      </table>
    </div>

    <el-dialog :title="course.id ? '编辑课程' : '新增课程'" :visible.sync="courseOpen" width="480px">
      <el-form label-width="72px" size="small">
        <el-form-item label="名称"><el-input v-model="course.title" /></el-form-item>
        <el-form-item label="阶段">
          <el-select v-model="course.stage" filterable allow-create default-first-option style="width:100%" placeholder="选择或输入新阶段">
            <el-option v-for="item in stages" :key="item" :label="stageName(item)" :value="item" />
          </el-select>
          <div class="stage-hint">已有阶段直接选。要新阶段，在这里输入名称，例如「华北特训」。</div>
        </el-form-item>
        <el-form-item label="课时"><span>一门课一份课件，记 1 课时。渠道要看完才算学完，不能点一下跳过。</span></el-form-item>
        <el-form-item label="课件">
          <button class="ch-btn" type="button" @click="$refs.courseFile.click()">上传视频或文档</button>
          <span v-if="course.fileName"> {{ course.fileName }}</span>
          <input ref="courseFile" class="file-proxy" type="file" accept="video/*,.pdf,.ppt,.pptx" @change="uploadCourseFile" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="course.status" style="width:100%">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="courseOpen = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveCourse">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="question.id ? '编辑题目' : '新增题目'" :visible.sync="questionOpen" width="560px">
      <el-form label-width="72px" size="small">
        <el-form-item label="阶段">
          <el-select v-model="question.stage" filterable allow-create default-first-option style="width:100%" placeholder="选择或输入新阶段">
            <el-option v-for="item in stages" :key="item" :label="stageName(item)" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="题型">
          <el-select v-model="question.questionType" style="width:100%">
            <el-option label="单选" value="SINGLE" />
            <el-option label="多选" value="MULTI" />
            <el-option label="判断" value="JUDGE" />
          </el-select>
        </el-form-item>
        <el-form-item label="题干"><el-input v-model="question.stem" type="textarea" :rows="3" /></el-form-item>
        <template v-if="question.questionType !== 'JUDGE'">
          <el-form-item label="A"><el-input v-model="question.optionA" /></el-form-item>
          <el-form-item label="B"><el-input v-model="question.optionB" /></el-form-item>
          <el-form-item label="C"><el-input v-model="question.optionC" /></el-form-item>
          <el-form-item label="D"><el-input v-model="question.optionD" /></el-form-item>
        </template>
        <el-form-item label="答案"><el-input v-model="question.answer" :placeholder="question.questionType === 'MULTI' ? '如 A,B' : question.questionType === 'JUDGE' ? '正确 或 错误' : 'A'" /></el-form-item>
        <el-form-item label="解析"><el-input v-model="question.analysis" type="textarea" :rows="2" /></el-form-item>
        <el-form-item label="状态">
          <el-select v-model="question.status" style="width:100%">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="questionOpen = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveQuestion">保存</el-button>
      </div>
    </el-dialog>
    <div v-if="viewer" class="viewer" @click.self="viewer = ''">
      <div class="viewer-card">
        <video v-if="isVideo(viewer)" :src="viewer" controls autoplay />
        <iframe v-else :src="viewer" title="课件" />
        <button class="ch-btn" type="button" @click="viewer = ''">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getChannelCourses, saveChannelCourse, getChannelQuestions, saveChannelQuestion, saveChannelExamConfig, saveChannelCertificate } from '@/api/admin'
import { uploadFile } from '@/api/upload'
import { normalizeFileUrl } from '@/utils/fileUrl'

const STAGE_LABELS = {
  '一阶': '第一阶产品培训',
  '二阶': '第二阶销售培训',
  '三阶': '第三阶解决方案培训',
  '结业': '结业'
}
const FALLBACK_STAGES = ['一阶', '二阶', '三阶', '结业']

export default {
  name: 'HqTraining',
  data() {
    return {
      tab: 'courses',
      tabs: [
        { id: 'courses', label: '课程库' },
        { id: 'progress', label: '学习进度' },
        { id: 'bank', label: '题库管理' },
        { id: 'stats', label: '考核统计与补考' },
        { id: 'certs', label: '证书管理' }
      ],
      stages: FALLBACK_STAGES.slice(),
      courses: [],
      progress: [],
      attempts: [],
      partners: [],
      questions: [],
      configForm: [],
      courseOpen: false,
      questionOpen: false,
      course: {},
      question: {},
      viewer: ''
    }
  },
  computed: {
    stageSummary() {
      return this.stages.filter(stage => stage !== '结业').map(stage => this.stageName(stage) + ' ' + this.courses.filter(row => row.stage === stage).length).join(' · ')
    },
    failedAttempts() {
      return this.attempts.filter(row => Number(row.passed) !== 1).length
    },
    passRate() {
      if (!this.attempts.length) return '—'
      const passed = this.attempts.filter(row => Number(row.passed) === 1).length
      return Math.round(passed * 1000 / this.attempts.length) / 10 + '%'
    },
    certified() {
      return this.partners.filter(row => row.certificateUrl).length
    }
  },
  created() { this.load() },
  methods: {
    stageName(stage) {
      return STAGE_LABELS[stage] || stage || '—'
    },
    isVideo(url) { return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || '') },
    openFile(url) { this.viewer = url },
    typeName(type) {
      return { SINGLE: '单选', MULTI: '多选', JUDGE: '判断' }[type] || '单选'
    },
    optionText(row) {
      if (row.questionType === 'JUDGE') return '正确 / 错误'
      return ['A', 'B', 'C', 'D'].filter(key => row['option' + key] && row['option' + key] !== '—').map(key => key + '. ' + row['option' + key]).join(' / ')
    },
    async load() {
      const courses = await getChannelCourses()
      this.courses = courses.list || []
      this.progress = courses.progress || []
      this.attempts = courses.attempts || []
      this.partners = courses.partners || []
      const exam = await getChannelQuestions()
      this.questions = exam.list || []
      const configs = exam.configs || []
      this.stages = (courses.stages && courses.stages.length) ? courses.stages : FALLBACK_STAGES.slice()
      this.configForm = this.stages.map(stage => {
        const found = configs.find(row => row.stage === stage)
        return found ? { stage, drawCount: Number(found.drawCount), passCount: Number(found.passCount) } : { stage, drawCount: '', passCount: '' }
      })
    },
    openCourse(row) {
      this.course = row ? { ...row, lessons: Number(row.lessons || 1), status: Number(row.status) === 0 ? 0 : 1, fileName: '' } : { title: '', stage: this.stages[0] || '', lessons: 1, fileUrl: '', status: 1, fileName: '' }
      this.courseOpen = true
    },
    async uploadCourseFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const result = await uploadFile(file, 'training', { useAdmin: true })
      this.course.fileUrl = normalizeFileUrl(result.url)
      this.course.fileName = file.name
    },
    async saveCourse() {
      if (!this.course.title) {
        this.$message.warning('请填写课程名称')
        return
      }
      await saveChannelCourse(this.course)
      this.courseOpen = false
      this.$message.success('课程已保存')
      this.load()
    },
    openQuestion(row) {
      this.question = row ? { ...row, status: Number(row.status) === 0 ? 0 : 1, questionType: row.questionType || 'SINGLE' } : { stage: this.stages[0] || '', questionType: 'SINGLE', stem: '', optionA: '', optionB: '', optionC: '', optionD: '', answer: 'A', analysis: '', status: 1 }
      this.questionOpen = true
    },
    async saveQuestion() {
      if (!this.question.stem) {
        this.$message.warning('请填写题干')
        return
      }
      await saveChannelQuestion(this.question)
      this.questionOpen = false
      this.$message.success('题目已保存')
      this.load()
    },
    async saveConfig() {
      const configs = this.configForm.filter(item => Number(item.drawCount) >= 1 && Number(item.passCount) >= 1)
      if (!configs.length) {
        this.$message.warning('请填写抽题数和达标数')
        return
      }
      await saveChannelExamConfig({ configs })
      this.$message.success('抽题配置已保存')
      this.load()
    },
    openCertificate(row) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*,.pdf'
      input.onchange = async () => {
        const file = input.files && input.files[0]
        if (!file) return
        const result = await uploadFile(file, 'training-certificate', { useAdmin: true })
        await saveChannelCertificate({ partnerId: row.id, fileUrl: normalizeFileUrl(result.url) })
        this.$message.success('证书已上传')
        this.load()
      }
      input.click()
    },
    exportCourses() {
      const lines = ['课程,阶段,课时,状态'].concat(this.courses.map(row => [row.title, this.stageName(row.stage), row.lessons, Number(row.status) === 0 ? '停用' : '启用'].join(',')))
      this.download('培训课程.csv', lines.join('\n'))
    },
    downloadTemplate() {
      this.download('题库模板.csv', '阶段,题型,题干,A,B,C,D,答案,解析\n一阶,单选,示例题干,选项A,选项B,选项C,选项D,A,解析')
    },
    download(name, text) {
      const blob = new Blob(['\ufeff' + text], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = name
      link.click()
      URL.revokeObjectURL(link.href)
    },
    async importCsv(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const text = await file.text()
      const rows = text.replace(/^\ufeff/, '').split(/\r?\n/).slice(1).filter(line => line.trim())
      for (const line of rows) {
        const cell = line.split(',')
        await saveChannelQuestion({
          stage: cell[0] || '一阶',
          questionType: cell[1] === '多选' ? 'MULTI' : cell[1] === '判断' ? 'JUDGE' : 'SINGLE',
          stem: cell[2],
          optionA: cell[3],
          optionB: cell[4],
          optionC: cell[5],
          optionD: cell[6],
          answer: cell[7],
          analysis: cell[8] || '',
          status: 1
        })
      }
      this.$message.success('已导入 ' + rows.length + ' 题')
      this.load()
    }
  }
}
</script>

<style scoped>
.configs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 12px 0; }
.config { border: 1px solid #e7ebf3; border-radius: 12px; padding: 12px; }
.config b { display: block; margin-bottom: 8px; color: #1a2468; }
.config label { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin: 6px 0; color: #5c6780; font-size: 13px; }
.config input { width: 72px; height: 32px; border: 1px solid #d9dfec; border-radius: 8px; padding: 0 8px; }
.bank-tools { display: flex; gap: 8px; margin: 14px 0; }
.file-proxy { position: fixed; left: 0; top: 0; width: 1px; height: 1px; opacity: 0; }
.stage-hint { margin-top: 6px; color: #8b93a7; font-size: 12px; line-height: 1.5; }
.viewer { position: fixed; inset: 0; z-index: 3000; background: rgba(16, 22, 56, 0.55); display: flex; align-items: center; justify-content: center; padding: 24px; }
.viewer-card { width: min(960px, 100%); background: #fff; border-radius: 12px; padding: 16px; }
.viewer-card video, .viewer-card iframe { width: 100%; height: min(70vh, 540px); background: #111; border: 0; }
.viewer-card .ch-btn { margin-top: 12px; }
.ch-metric small { font-size: 14px; font-weight: 500; }
@media (max-width: 1100px) { .configs { grid-template-columns: 1fr; } }
</style>
